/**
 * 角色定義與顯示名稱
 * 
 * 這是系統中所有角色的 Single Source of Truth
 * 所有角色判斷都應該參考這個檔案
 * 
 * @module Roles
 */

/**
 * 系統角色類型
 */
export type AdminRole = 'owner' | 'super_admin' | 'store_manager' | 'staff' | 'part_time';

/**
 * 角色顯示名稱映射
 */
export const roleNames: Record<AdminRole, string> = {
  owner: '全知者',
  super_admin: '總管理',
  store_manager: '店長',
  staff: '員工',
  part_time: '工讀生',
};

/**
 * 角色層級（數字越大權限越高）
 * 
 * 注意：這個層級僅用於基本比較，不代表完整的權限關係
 * 實際權限應該查詢 permissions.ts 的權限矩陣
 */
export const roleLevel: Record<AdminRole, number> = {
  owner: 100,
  super_admin: 80,
  store_manager: 60,
  staff: 40,
  part_time: 20,
};

/**
 * 檢查角色是否有效
 */
export function isValidRole(role: string): role is AdminRole {
  return ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'].includes(role);
}

/**
 * 將舊版或顯示用角色字串轉成 canonical role。
 */
export function normalizeAdminRole(role: unknown): AdminRole | null {
  if (typeof role !== 'string') return null;
  const normalized = role.trim().toLowerCase();
  const aliases: Record<string, AdminRole> = {
    owner: 'owner',
    '全知者': 'owner',
    super_admin: 'super_admin',
    superadmin: 'super_admin',
    'super-admin': 'super_admin',
    '總管理': 'super_admin',
    store_manager: 'store_manager',
    manager: 'store_manager',
    '店長': 'store_manager',
    staff: 'staff',
    '員工': 'staff',
    part_time: 'part_time',
    parttime: 'part_time',
    'part-time': 'part_time',
    '工讀生': 'part_time',
  };
  return aliases[normalized] ?? null;
}

/**
 * 取得角色顯示名稱
 */
export function getRoleName(role: AdminRole): string {
  return roleNames[role] || '未知角色';
}

/**
 * 比較兩個角色的層級
 * @returns true 如果 role1 的層級 >= role2
 */
export function hasHigherOrEqualRole(role1: AdminRole, role2: AdminRole): boolean {
  return roleLevel[role1] >= roleLevel[role2];
}

/**
 * 取得指定角色可以管理的角色清單
 * 
 * 規則：
 * - owner: 可管理所有角色（owner, super_admin, store_manager, staff, part_time）
 * - super_admin: 可管理 super_admin, store_manager, staff, part_time（不能管理 owner）
 * - store_manager: 暫不開放編輯/停用管理員
 * - 其他角色: 無管理權限
 * 
 * @param callerRole - 呼叫者角色
 * @returns 可管理的角色清單
 */
export function getManageableRoles(callerRole: AdminRole): AdminRole[] {
  switch (callerRole) {
    case 'owner':
      // owner 可管理所有角色
      return ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'];

    case 'super_admin':
      // super_admin 可管理非 owner 角色
      return ['super_admin', 'store_manager', 'staff', 'part_time'];

    default:
      // 其他角色無管理權限
      return [];
  }
}

/**
 * 取得指定角色可以邀請建立的角色清單。
 *
 * 邀請權限與編輯/停用權限分離，避免 store_manager 取得管理既有 admin 的能力。
 */
export function getInvitableRoles(callerRole: AdminRole): AdminRole[] {
  switch (callerRole) {
    case 'owner':
      return ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'];
    case 'super_admin':
      return ['super_admin', 'store_manager', 'staff', 'part_time'];
    case 'store_manager':
      return ['staff', 'part_time'];
    default:
      return [];
  }
}

/**
 * 檢查呼叫者是否可以管理目標角色
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以管理
 */
export function canManageRole(callerRole: AdminRole, targetRole: AdminRole): boolean {
  const manageableRoles = getManageableRoles(callerRole);
  return manageableRoles.includes(targetRole);
}

/**
 * 檢查呼叫者是否可以編輯目標管理員
 * 
 * 規則：
 * - owner 可編輯所有人
 * - super_admin 可編輯 super_admin, store_manager, staff, part_time
 * - store_manager 暫不開放編輯
 * - 其他角色無編輯權限
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以編輯
 */
export function canEditAdmin(callerRole: AdminRole, targetRole: AdminRole): boolean {
  return canManageRole(callerRole, targetRole);
}

/**
 * 檢查呼叫者是否可以刪除目標管理員
 * 
 * 規則：
 * - owner 可停用所有角色
 * - super_admin 可停用 super_admin, store_manager, staff, part_time
 * - store_manager 暫不開放停用
 * - owner 本身不可被停用（需由後端最後 owner / self guard 阻擋）
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以刪除
 */
export function canDeleteAdmin(callerRole: AdminRole, targetRole: AdminRole): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole === 'super_admin') return targetRole !== 'owner';
  return false;
}

/**
 * 檢查呼叫者是否可以重設目標管理員密碼
 * 
 * 規則：
 * - owner 可重設所有人密碼
 * - super_admin 只能重設 store_manager, staff, part_time 密碼
 * - 其他角色無重設權限
 * 
 * @param callerRole - 呼叫者角色
 * @param targetRole - 目標角色
 * @returns true 如果可以重設密碼
 */
export function canResetPassword(callerRole: AdminRole, targetRole: AdminRole): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole === 'super_admin') {
    return ['store_manager', 'staff', 'part_time'].includes(targetRole);
  }
  return false;
}
